'use client'

import type { InputProps } from '@pearlconvert/ui/components/input/input.types'
import type { FieldValues } from 'react-hook-form'

import classNames from 'classnames'
import { useTranslations } from 'next-intl'
import React from 'react'

import { Button } from '@pearlconvert/ui/components/button'
import { useInput } from '@pearlconvert/ui/components/input/batons/use-input'
import { Progressbar } from '@pearlconvert/ui/components/progressbar'
import { getSuppressTracking } from '@pearlconvert/ui/utils/suppression.utils'

import '@pearlconvert/ui/components/input/styles/input.scss'

const Input = React.forwardRef(
  <TFieldValues extends FieldValues>(
    {
      id,
      label,
      isLabelVisible = true,
      inputSize,
      contrast = false,
      success = false,
      error,
      hint,
      hintClassName,
      readonly = false,
      icon,
      optionalText,
      className,
      trigger,
      onChange,
      onBlur,
      onFocus,
      ...inputProps
    }: InputProps<TFieldValues>,
    forwardRef: React.Ref<HTMLInputElement> | undefined
  ) => {
    const t = useTranslations('ui.input.password')
    const rootClass = classNames('input__root', {
      'input__root--has-text-optional':
        optionalText || inputProps.type === 'password',
    })

    const wrapperClass = classNames('input__wrapper', {
      [`input__wrapper--icon-${icon}`]: icon,
      [`${className}--wrapper`]: className,
    })

    const inputClass = classNames(
      'input__field',
      {
        [`input__field--${inputSize}`]: inputSize,
        'input__field--error': error,
        'input__field--contrast': contrast,
        'input__field--success': success,
        'input__field--icon': icon,
      },
      className
    )

    const {
      handleBlur,
      handleFocus,
      handleChange,
      handlePhoneInputKeyDown,
      showPassword,
      togglePasswordVisibility,
      passwordStrength,
    } = useInput<TFieldValues>({
      id,
      type: inputProps.type,
      error,
      trigger,
    })

    return (
      <div className={rootClass}>
        {label && (
          <label
            className={classNames('input__label', {
              'sr-only': !isLabelVisible,
            })}
            htmlFor={id}
          >
            {label}
          </label>
        )}
        {optionalText && (
          <span className="input__label--optional">{optionalText}</span>
        )}
        {inputProps.type === 'password' && (
          <Button
            className="input__toggle-password"
            size="small"
            type="link"
            role="button"
            buttonType="button"
            iconLeft={`eye-${showPassword ? 'closed' : 'opened'}`}
            onClick={togglePasswordVisibility}
            isToggle={true}
          >
            {showPassword ? t('hide') : t('show')}
          </Button>
        )}
        <div className={wrapperClass}>
          <input
            ref={forwardRef}
            id={id}
            className={inputClass}
            aria-invalid={error !== undefined}
            {...((inputProps.autoComplete === 'new-password' &&
              passwordStrength && { 'aria-describedby': 'strength__' + id }) ||
              (error && { 'aria-describedby': 'error__' + id }) ||
              (hint && { 'aria-describedby': 'hint__' + id }) ||
              (error && hint && { 'aria-describedby': 'error__' + id }))}
            onBlur={(e) => {
              handleBlur(e)
              onBlur?.(e)
            }}
            onChange={(e) => {
              handleChange(e)
              onChange?.(e)
            }}
            onFocus={(e) => {
              handleFocus()
              onFocus?.(e)
            }}
            onKeyDown={
              inputProps.type === 'tel'
                ? handlePhoneInputKeyDown
                : inputProps.onKeyDown
            }
            {...(inputProps.type === 'email' ||
            inputProps.type === 'password' ||
            inputProps.type === 'tel'
              ? getSuppressTracking()
              : {})}
            {...inputProps}
            readOnly={readonly}
            type={
              inputProps.type === 'password' && showPassword
                ? 'text'
                : inputProps.type
            }
          />
        </div>
        {inputProps.autoComplete === 'new-password' && passwordStrength && (
          <div
            className={classNames(
              'input__password-strength',
              `input__password-strength__strength-${passwordStrength.identifier}`
            )}
          >
            <Progressbar progress={passwordStrength.progress} />
            <p
              className="input__password-strength-hint"
              id={'strength__' + id}
              role="alert"
              aria-hidden={false}
              aria-live="polite"
            >
              {t('strength.label', {
                strength: t(`strength.${passwordStrength.identifier}`),
              })}
            </p>
          </div>
        )}
        {error && (
          <p
            className="input__message"
            id={'error__' + id}
            role="alert"
            aria-hidden={false}
            aria-live="polite"
          >
            {error}
          </p>
        )}
        {hint && (
          <p
            className={
              hintClassName ||
              `input__hint${readonly ? ' input__hint--readonly' : ''}`
            }
            id={'hint__' + id}
            aria-live="polite"
            aria-hidden={false}
          >
            {hint}
          </p>
        )}
      </div>
    )
  }
)

Input.displayName = 'Input'
export default Input
